import { doesFocusableExist, setFocus } from '@noriginmedia/norigin-spatial-navigation';
import {
  clearSearch,
  playVideo,
  resetVideo,
  setPageOptions,
  setVideoOptions
} from 'common/actions';
import { DateTime } from 'common/components/date-time';
import { IdleTimerOverlay } from 'common/components/idle-timer';
import { LastWatchedChannelHint } from 'common/components/last-watched-channel-hint';
import { HAS_ACCESS, ROUTE_HOME, ROUTE_ROOT, ROUTE_SCREENSAVER } from 'common/config/constants';
import variables from 'common/config/variables';
import { Epg, LiveChannel, LiveChannels } from 'common/constants/data-types';
import { usePopup } from 'common/contexts/popup';
import { AppConfigState, PageOptions } from 'common/reducers/appConfig';
import { ICarousel } from 'common/reducers/carousel';
import { Errors } from 'common/reducers/errors';
import { CLOCK_POSITIONS, ProfileSettings } from 'common/reducers/profileSettings';
import { IVideoPlayer } from 'common/reducers/videoPlayer';
import { getLastWatchedChannelId, setLastWatchedChannelId } from 'common/services/helpers';
import { closeCurrentApp, getClassName, getDeviceType } from 'common/utils';
import FocusHistory from 'common/utils/FocusHistory';
import { useParentalLock, useRequiredData, useVisibilityState } from 'common/utils/hooks';
import { useIdleTimer } from 'common/utils/hooks/useIdleTimer';
import { StoreState } from 'index';
import { isEmpty } from 'lodash';
import SideMenu from 'menu/SideMenu';
import { isMenuShown } from 'menu/utils';
import Pages from 'pages';
import { Player } from 'Player';
import React, { useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { connect, shallowEqual, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

const DEVICE_TYPE = getDeviceType();

interface LinearProps {
  hasErrors?: boolean;
  liveChannels?: LiveChannels;
  isPlayerOpenned?: boolean;
  isPlayerFullscreen?: boolean;
  isPlayerPlaying?: boolean;
  currentPlayingEpg?: Epg;
  setVideoOptions: any;
  userDetails: any;
  isPlayingFromBanner?: boolean;
  playVideo: (currentepg: Epg, options: any) => void;
  resetVideo: VoidFunction;
  clearSearch: VoidFunction;
  pageWrapperActive?: boolean;
  unMountPage?: boolean;
  setPageOptions: (options: Partial<PageOptions>) => void;
}

function Linear(props: LinearProps) {
  const { idleMessageShown, screenSaverActive } = useIdleTimer({});
  const { hasPopups } = usePopup();
  const location = useLocation();
  const navigate = useNavigate();

  const onAppHidden = useCallback(() => {
    // Do not close the app on TitanOS or Vidaa
    if (['titanos', 'vidaa'].includes(DEVICE_TYPE)) return;

    closeCurrentApp();
  }, []);

  const onAppVisible = useCallback(() => {
    // Do not reload the app on TitanOS or Vidaa as the app is not closed on those platforms
    if (['titanos', 'vidaa'].includes(DEVICE_TYPE)) return;

    window.location.reload();
  }, []);

  const isAppActive = useVisibilityState({ onHidden: onAppHidden, onVisible: onAppVisible });
  const shouldResetToHomePosition = useRef(true);
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const unMountPageTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const { loadingRequiredData } = useRequiredData();
  const disableFocusOnWrapperRef = useRef<boolean>(false);
  const liveChannelsRef = useRef(props.liveChannels);
  const [firstLaunch, setFirstLaunch] = useState(true);

  const [menuFocused, setMenuFocused] = useState(false);
  const menuIsShown = useMemo(() => isMenuShown(location.pathname), [location.pathname]);
  const setPageWrapperActive = useCallback((value: boolean) => {
    props.setPageOptions({
      pageWrapperActive: value
    });
  }, []);
  const { getChannelsLockedState } = useParentalLock();

  const [, startTransition] = useTransition();

  const loadedLiveChannels = useMemo(() => {
    liveChannelsRef.current = props.liveChannels;
    return !isEmpty(props.liveChannels);
  }, [props.liveChannels]);
  const setUnMountPage = useCallback((value: boolean) => {
    props.setPageOptions({
      unmountPage: value
    });
  }, []);
  const pageWrapperActive = props.pageWrapperActive;
  const unMountPage = props.unMountPage;

  const isPlayerOpennedRef = useRef(props.isPlayerOpenned);

  useEffect(() => {
    isPlayerOpennedRef.current = props.isPlayerOpenned;
  }, [props.isPlayerOpenned]);

  const returnToPlayer = useCallback(() => {
    if (isPlayerOpennedRef.current) {
      props.setVideoOptions({
        isFullscreen: true
      });
    }
  }, []);

  // Save last played channel to memory
  useEffect(() => {
    if (!isEmpty(props.currentPlayingEpg?.chan_id) && props.currentPlayingEpg?.chan_id) {
      setLastWatchedChannelId(props.currentPlayingEpg.chan_id);
    }
  }, [props.currentPlayingEpg?.chan_id]);

  const checkChannelForAccess = useCallback((channel: LiveChannel) => {
    return (
      channel.access === HAS_ACCESS &&
      !(channel.locked && getChannelsLockedState()) &&
      channel.visible
    );
  }, []);

  const lastWatchedChannelId = useMemo(() => {
    if (isEmpty(liveChannelsRef.current)) {
      return null;
    }
    const liveChannelsArray = Object.values(liveChannelsRef.current);
    const lastSavedChannelId = getLastWatchedChannelId();
    const lastSavedChannel = liveChannelsRef.current[lastSavedChannelId];

    return lastSavedChannel && checkChannelForAccess(lastSavedChannel)
      ? lastSavedChannel.id
      : // If last saved channel is not available, return the first available channel
        // fallback to original channel
        liveChannelsArray.find(checkChannelForAccess)?.id || lastSavedChannel.id;
  }, [loadedLiveChannels, isAppActive]);

  const showPageWrapper = useCallback(() => {
    setPageWrapperActive(true);
    disableFocusOnWrapperRef.current = false;
  }, []);

  const canPlayVideo =
    loadedLiveChannels &&
    lastWatchedChannelId &&
    isAppActive &&
    !props.hasErrors &&
    !isEmpty(props.userDetails) &&
    !isPlayerOpennedRef.current &&
    !screenSaverActive;

  const loadedLauncher = useMemo(
    () => location.pathname !== ROUTE_ROOT && !loadingRequiredData,
    [location.pathname, loadingRequiredData]
  );

  // Handle initial player loading
  // Load stream when livechannels are available, when app is in active state
  // and when userdetails are available.
  // If for some reason player resets itself, reload the last watched channel.
  useEffect(() => {
    try {
      if (idleMessageShown) throw new Error('Idle message is shown, not loading video');

      if (canPlayVideo && liveChannelsRef.current) {
        const channel = liveChannelsRef.current[lastWatchedChannelId];
        props.playVideo(channel.currentepg, {
          startPosition: -1,
          channelPosition: channel.position
        });
      } else if (canPlayVideo && loadedLauncher) {
        setFocus('MENU');
        // Force call onMenuFocus to avoid flickering of page
        onMenuFocus();
        disableFocusOnWrapperRef.current = true;
      } else if (isAppActive) {
        showPageWrapper();
      }
    } catch (error) {
      console.info(error);
      // Show screen for fallback
      showPageWrapper();
      setUnMountPage(false);
    }
  }, [canPlayVideo, lastWatchedChannelId]);
  //

  useEffect(() => {
    if (!isAppActive) {
      setFirstLaunch(true);
    }
  }, [isAppActive]);

  useEffect(() => {
    if (!props.isPlayerOpenned) {
      showPageWrapper();
      setUnMountPage(false);
    }
  }, [props.isPlayerOpenned]);

  // Focus on page when the page comes into view
  useEffect(() => {
    if (pageWrapperActive && isAppActive && !disableFocusOnWrapperRef.current) {
      const key = `${location.pathname}-stripe-view`;
      // Delay focus to next tick to allow the element to be focusable
      setTimeout(() => {
        doesFocusableExist(key) && setFocus(key);
      });
    }
  }, [pageWrapperActive, isAppActive]);
  //

  // Reset hide status of page wrapper when player comes on the front
  // Reset page position to HOME
  useEffect(() => {
    if (props.isPlayerFullscreen) {
      shouldResetToHomePosition.current && navigate(ROUTE_HOME);
      setPageWrapperActive(false);
    }
  }, [props.isPlayerFullscreen]);

  // Handle return to player when no activity has been tracked
  useEffect(() => {
    const handleTimer = () => {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = setTimeout(() => {
        returnToPlayer();
        FocusHistory.clearHistory();
        props.clearSearch();
        shouldResetToHomePosition.current = true;
      }, variables.AUTO_SHOW_PLAYER_TIMEOUT);
    };
    // Track activity only when page wrapper is active
    // and no popups are on stop of the screen
    // Do not track activity when a banner is playing a preview
    // Do not track activity when errors are present
    // Do not track activity when the player is not openned
    if (
      pageWrapperActive &&
      !props.hasErrors &&
      !hasPopups &&
      !props.isPlayingFromBanner &&
      props.isPlayerOpenned
    ) {
      document.addEventListener('keydown', handleTimer);
      document.addEventListener('mousemove', handleTimer);
      handleTimer();
    }
    return () => {
      document.removeEventListener('keydown', handleTimer);
      document.removeEventListener('mousemove', handleTimer);
      clearTimeout(inactivityTimeoutRef.current);
    };
  }, [
    pageWrapperActive,
    hasPopups,
    props.isPlayingFromBanner,
    props.hasErrors,
    props.isPlayerOpenned
  ]);

  // Sync to mount page when page wrapper is active
  useEffect(() => {
    if (pageWrapperActive) {
      startTransition(() => setUnMountPage(false));
    }
  }, [pageWrapperActive]);

  const onPlayerBack = useCallback(() => {
    if (shouldResetToHomePosition.current) {
      setFocus('MENU');
    } else {
      props.setVideoOptions({
        isFullscreen: false
      });
      startTransition(() => {
        showPageWrapper();
      });
      disableFocusOnWrapperRef.current = false;
    }
    setFirstLaunch(false);
  }, []);

  const onMenuFocus = useCallback(() => {
    setPageWrapperActive(false);
    setMenuFocused(true);
    props.setVideoOptions({
      isFullscreen: false
    });
    shouldResetToHomePosition.current = true;
    unMountPageTimeoutRef.current = setTimeout(() => {
      startTransition(() => setUnMountPage(false));
    }, variables.LONG_THROTTLE_TIMEOUT);
  }, []);

  const onMenuBlur = useCallback(() => {
    setMenuFocused(false);
  }, []);

  const onMenuSelect = useCallback(() => {
    props.setVideoOptions({
      isFullscreen: false
    });
    startTransition(() => showPageWrapper());
    disableFocusOnWrapperRef.current = false;
    shouldResetToHomePosition.current = false;
    setFirstLaunch(false);
  }, []);

  const onPlayerFocus = useCallback(() => {
    if (!pageWrapperActive) {
      returnToPlayer();
      clearTimeout(unMountPageTimeoutRef.current);
      shouldResetToHomePosition.current && startTransition(() => setUnMountPage(true));
    }
  }, [returnToPlayer, pageWrapperActive]);

  const clockPosition = useSelector(
    (state: StoreState) =>
      (state.profileSettings as ProfileSettings)?.react_tv_settings?.clockPosition,
    shallowEqual
  );

  const onIdleTimerBack = useCallback(() => {
    clearTimeout(inactivityTimeoutRef.current);
    setFirstLaunch(true);
    setFocus('home-menu-item');
    props.resetVideo();
    showPageWrapper();
    navigate(ROUTE_SCREENSAVER);
  }, [props.resetVideo]);

  const onPageWrapperFocus = useCallback(() => {
    startTransition(() => showPageWrapper());
    disableFocusOnWrapperRef.current = false;
    shouldResetToHomePosition.current = false;
  }, []);

  return (
    <div className="linear-view-container">
      {lastWatchedChannelId && menuIsShown && (
        <LastWatchedChannelHint
          channelId={lastWatchedChannelId}
          visible={menuFocused && firstLaunch}
        />
      )}
      <SideMenu
        focusKey="MENU"
        onFocus={onMenuFocus}
        onBlur={onMenuBlur}
        onMenuSelect={onMenuSelect}
        initialOpen={firstLaunch}
      />
      {!unMountPage && (
        <Pages
          className={getClassName('linear-page-wrapper', {
            hidden: !pageWrapperActive,
            'has-background': props.isPlayingFromBanner
          })}
          onFocus={onPageWrapperFocus}
        />
      )}
      <IdleTimerOverlay onBack={onIdleTimerBack} />
      {loadedLauncher && (
        <Player
          className="linear-player"
          onBack={onPlayerBack}
          onFocus={onPlayerFocus}
          paused={props.isPlayingFromBanner || !loadedLauncher}
        />
      )}
      {props.isPlayerOpenned && clockPosition === CLOCK_POSITIONS.ALWAYS && (
        <DateTime clockPosition={clockPosition} />
      )}
    </div>
  );
}

interface LinearStore {
  videoPlayer: IVideoPlayer;
  liveChannels: LiveChannels;
  userDetails: any;
  errors: Errors;
  carousel: ICarousel;
  appConfig: AppConfigState;
}
const mapStateToProps = ({
  videoPlayer,
  liveChannels,
  userDetails,
  errors,
  carousel,
  appConfig
}: LinearStore) => {
  return {
    isPlayerOpenned: videoPlayer?.isOpen,
    isPlayerFullscreen: videoPlayer?.video?.isFullscreen,
    isPlayerPlaying: videoPlayer?.isPlaying,
    currentPlayingEpg: videoPlayer?.epgs?.current,
    liveChannels,
    userDetails,
    hasErrors: Object.values(errors).every((e) => !!e),
    isPlayingFromBanner: carousel.isBannerPlaying,
    pageWrapperActive: !!appConfig.pageOptions?.pageWrapperActive,
    unMountPage: !!appConfig.pageOptions?.unmountPage
  };
};

export default React.memo(
  connect(mapStateToProps, { setVideoOptions, setPageOptions, playVideo, resetVideo, clearSearch })(
    Linear
  )
);
